import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Box,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import FacebookIcon from '@mui/icons-material/Facebook';
import MenuIcon from '@mui/icons-material/Menu';
import ContactModal from '../ContactModal';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import StyledButton from '../common/StyledButton';
import StagingBanner from '../common/StagingBanner';
import { FESTIVAL_HOURS_LABEL } from '../../utils/festivalDates';

const MainLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [hasVendorProfile, setHasVendorProfile] = useState(false);
  const [vendorProfile, setVendorProfile] = useState(null);
  const [boothRegistration, setBoothRegistration] = useState(null);
  const [registrationStatus, setRegistrationStatus] = useState(null);
  const { isAuthenticated, logout, user, isAdmin, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  console.log('MainLayout: Auth state:', { 
    isAuthenticated, 
    isAdmin, 
    userRole: user?.role,
    user: user 
  });

  useEffect(() => {
    const checkVendorProfile = async () => {
      if (isAuthenticated && user) {
        try {
          const [profileRes, statusRes, boothRes] = await Promise.all([
            api.get('/vendor/profile'),
            api.get('/vendor/registration-status'),
            api.get('/vendor/booth-registration')
          ]);
          
          setHasVendorProfile(true);
          setVendorProfile(profileRes.data);
          setRegistrationStatus(statusRes.data);
          
          // If we have both profile and booth data but status isn't complete,
          // update the registration status
          if (boothRes.data.length > 0 && statusRes.data.registration_status !== 'complete') {
            try {
              await api.put('/vendor/registration-step', {
                step: 'booth_selection'
              });
              setRegistrationStatus({ ...statusRes.data, registration_status: 'complete' });
            } catch (err) {
              console.error('Failed to update registration status:', err);
            }
          }
        } catch (error) {
          console.error('Error fetching vendor data:', error);
          setHasVendorProfile(false);
          setVendorProfile(null);
          setRegistrationStatus(null);
        }
      } else {
        setHasVendorProfile(false);
        setVendorProfile(null);
        setRegistrationStatus(null);
      }
    };
    checkVendorProfile();
  }, [isAuthenticated, user]);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleContactClick = () => {
    setContactModalOpen(true);
  };

  const handleCloseContactModal = () => {
    setContactModalOpen(false);
  };

  const handleLogout = () => {
    logout(() => {
      navigate('/');
    });
  };

  const renderAuthButtons = () => {
    if (isAuthenticated) {
      // Older users were missing the Vendor Dashboard link buried in the
      // hamburger drawer. Surface it (or the Admin Dashboard) directly in
      // the header where the Sign Up button used to live.
      if (isAdmin) {
        return (
          <StyledButton
            component={Link}
            to="/admin"
            variant="contained"
          >
            Admin Dashboard
          </StyledButton>
        );
      }
      if (hasVendorProfile) {
        return (
          <StyledButton
            component={Link}
            to="/vendor/dashboard"
            variant="contained"
          >
            Vendor Dashboard
          </StyledButton>
        );
      }
      if (registrationStatus?.registration_status === 'incomplete') {
        return (
          <StyledButton
            component={Link}
            to={`/vendor/onboarding?step=${registrationStatus.registration_step}`}
            variant="contained"
          >
            Resume Registration
          </StyledButton>
        );
      }
      return null;
    }
    if (registrationStatus?.registration_status === 'incomplete') {
      return (
        <StyledButton
          component={Link}
          to="/vendor/onboarding"
          variant="contained"
        >
          Resume Registration
        </StyledButton>
      );
    }
    // Only show Sign Up button if not mobile
    if (!isMobile) {
      return (
        <StyledButton
          component={Link}
          to="/signup"
          variant="contained"
        >
          Sign Up
        </StyledButton>
      );
    }
    return null;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <StagingBanner />
      <AppBar position="sticky" color="primary" elevation={0}>
        <Toolbar>
          <Typography variant="h6" component={Link} to="/" sx={{ flexGrow: 1, color: '#222', fontWeight: 700, textDecoration: 'none' }}>
            Gingham Tree
          </Typography>
          {!isMobile && (
            <>
              {/* Desktop nav stays slim: only the three primary actions live
                  in the bar. Secondary links (Visitors, Exhibitor Info,
                  Contact) live in the right-side drawer so the header never
                  crams or wraps at mid-width breakpoints. */}
              <Button
                component={Link}
                to="/map"
                color="inherit"
                sx={{ color: '#222', mr: 1, fontSize: '1rem', whiteSpace: 'nowrap' }}
              >
                Map
              </Button>
              <Button
                component={Link}
                to="/directions"
                color="inherit"
                sx={{ color: '#222', mr: 1, fontSize: '1rem', whiteSpace: 'nowrap' }}
              >
                Directions
              </Button>
              <Button
                component={Link}
                to="/vendors"
                color="inherit"
                sx={{ color: '#222', mr: 2, fontSize: '1rem', whiteSpace: 'nowrap' }}
              >
                Vendors
              </Button>
              {renderAuthButtons()}
            </>
          )}
          <IconButton
            component="a"
            href="https://www.facebook.com/GinghamTree/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Facebook"
            sx={{ color: '#4267B2', mr: 1 }}
          >
            <FacebookIcon fontSize="medium" />
          </IconButton>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="end"
            onClick={toggleSidebar}
            sx={{ ml: 1 }}
          >
            <MenuIcon />
          </IconButton>
        </Toolbar>
        {isMobile && (
          <Toolbar
            sx={{
              backgroundColor: 'white',
              borderTop: '1px solid #eee',
              justifyContent: 'center',
              gap: 2,
              py: 1,
            }}
          >
            {/* Primary mobile nav mirrors the three home-page actions so
                older users find the same labels everywhere. Secondary
                links (Visitors / Exhibitor Info / Contact) live in the
                right-hand drawer to keep this row uncluttered. */}
            <Button
              component={Link}
              to="/map"
              color="inherit"
              size="small"
              sx={{ color: '#222', fontWeight: 600 }}
            >
              Map
            </Button>
            <Button
              component={Link}
              to="/directions"
              color="inherit"
              size="small"
              sx={{ color: '#222', fontWeight: 600 }}
            >
              Directions
            </Button>
            <Button
              component={Link}
              to="/vendors"
              color="inherit"
              size="small"
              sx={{ color: '#222', fontWeight: 600 }}
            >
              Vendors
            </Button>
            {isAuthenticated && isAdmin && (
              <StyledButton
                component={Link}
                to="/admin"
                variant="contained"
                size="small"
              >
                Admin Dashboard
              </StyledButton>
            )}
            {isAuthenticated && !isAdmin && hasVendorProfile && (
              <StyledButton
                component={Link}
                to="/vendor/dashboard"
                variant="contained"
                size="small"
              >
                Vendor Dashboard
              </StyledButton>
            )}
            {isAuthenticated && !isAdmin && !hasVendorProfile && registrationStatus?.registration_status === 'incomplete' && (
              <StyledButton
                component={Link}
                to={`/vendor/onboarding?step=${registrationStatus.registration_step}`}
                variant="contained"
                size="small"
              >
                Resume Registration
              </StyledButton>
            )}
            {!isAuthenticated && registrationStatus?.registration_status === 'incomplete' && (
              <StyledButton
                component={Link}
                to="/vendor/onboarding"
                variant="contained"
                size="small"
              >
                Resume Registration
              </StyledButton>
            )}
          </Toolbar>
        )}
      </AppBar>

      <Drawer
        anchor="right"
        open={sidebarOpen}
        onClose={toggleSidebar}
      >
        <Box sx={{ width: 250 }}>
          <List>
            {isAuthenticated ? (
              <>
                <ListItem>
                  <ListItemText
                    primary={user?.email}
                    secondary={
                      isSuperAdmin
                        ? 'Super Admin Account'
                        : isAdmin
                          ? 'Admin Account'
                          : (vendorProfile?.business_name || 'User Account')
                    }
                  />
                </ListItem>
                <Divider />
                <ListItem button component={Link} to="/visitors" onClick={toggleSidebar}>
                  <ListItemText primary="For Visitors" />
                </ListItem>
                <ListItem button component={Link} to="/exhibitor-info" onClick={toggleSidebar}>
                  <ListItemText primary="For Exhibitors" />
                </ListItem>
                <ListItem
                  button
                  onClick={() => {
                    handleContactClick();
                    toggleSidebar();
                  }}
                >
                  <ListItemText primary="Contact" />
                </ListItem>
                <Divider />
                {isAdmin ? (
                  <ListItem button component={Link} to="/admin" onClick={toggleSidebar}>
                    <ListItemText
                      primary="Admin Dashboard"
                      secondary="Use the sidebar inside for the full admin menu"
                    />
                  </ListItem>
                ) : (
                  <>
                    {hasVendorProfile && (
                      <ListItem button component={Link} to="/vendor/dashboard" onClick={toggleSidebar}>
                        <ListItemText primary="Vendor Dashboard" />
                      </ListItem>
                    )}
                    {registrationStatus?.registration_status === 'incomplete' && (
                      <ListItem 
                        button 
                        component={Link} 
                        to={`/vendor/onboarding?step=${registrationStatus.registration_step}`}
                        onClick={toggleSidebar}
                      >
                        <ListItemText primary="Resume Registration" />
                      </ListItem>
                    )}
                  </>
                )}
                <ListItem button onClick={() => { handleLogout(); toggleSidebar(); }}>
                  <ListItemText primary="Logout" />
                </ListItem>
              </>
            ) : (
              <>
                <ListItem button component={Link} to="/map" onClick={toggleSidebar}>
                  <ListItemText primary="Map" />
                </ListItem>
                <ListItem button component={Link} to="/directions" onClick={toggleSidebar}>
                  <ListItemText primary="Directions" />
                </ListItem>
                <ListItem button component={Link} to="/vendors" onClick={toggleSidebar}>
                  <ListItemText primary="Vendors" />
                </ListItem>
                <Divider />
                <ListItem button component={Link} to="/visitors" onClick={toggleSidebar}>
                  <ListItemText primary="For Visitors" />
                </ListItem>
                <ListItem button component={Link} to="/exhibitor-info" onClick={toggleSidebar}>
                  <ListItemText primary="For Exhibitors" />
                </ListItem>
                <ListItem
                  button
                  onClick={() => {
                    handleContactClick();
                    toggleSidebar();
                  }}
                >
                  <ListItemText primary="Contact" />
                </ListItem>
                <Divider />
                {registrationStatus?.registration_status === 'incomplete' ? (
                  <ListItem
                    button
                    component={Link}
                    to={`/vendor/onboarding?step=${registrationStatus.registration_step}`}
                    onClick={toggleSidebar}
                  >
                    <ListItemText primary="Resume Registration" />
                  </ListItem>
                ) : (
                  <>
                    <ListItem button component={Link} to="/login" onClick={toggleSidebar}>
                      <ListItemText primary="Login" />
                    </ListItem>
                    <ListItem button component={Link} to="/signup" onClick={toggleSidebar}>
                      <ListItemText primary="Sign Up" />
                    </ListItem>
                  </>
                )}
              </>
            )}
          </List>
        </Box>
      </Drawer>

      <Box component="main" sx={{ flexGrow: 1 }}>
        {children}
      </Box>

      <ContactModal
        open={contactModalOpen}
        onClose={handleCloseContactModal}
      />

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          width: '100%',
          borderTop: '1px solid #eee',
          mt: 4,
          py: 2.5,
          background: '#fafbfc',
          textAlign: 'center',
        }}
      >
        <Typography variant="body2" sx={{ color: '#888', mb: 0.5 }}>
          Hosted by Lucedale Fine Arts Club, 2026
        </Typography>
        <Typography variant="body2" sx={{ color: '#888', mb: 0.5 }}>
          George County Fairgrounds &middot; 9162 Old Hwy 63, Lucedale, MS 39452
        </Typography>
        <Typography variant="body2" sx={{ color: '#888', mb: 0.5 }}>
          Festival hours: {FESTIVAL_HOURS_LABEL}
        </Typography>
        <Typography variant="body2" sx={{ color: '#888', mb: 0.5 }}>
          Email: <a href="mailto:contact@ginghamtree.com" style={{ color: '#888', textDecoration: 'underline dotted' }}>contact@ginghamtree.com</a>
        </Typography>
        <Typography variant="body2" sx={{ color: '#888', mb: 0.5 }}>
          Site by: <a href="https://saltmarshdigital.com" target="_blank" rel="noopener noreferrer" style={{ color: '#888', textDecoration: 'underline dotted' }}>Salt Marsh Digital</a>
        </Typography>
        <Typography variant="body2" sx={{ color: '#888' }}>
          Facebook: <a href="https://www.facebook.com/GinghamTree/" target="_blank" rel="noopener noreferrer" style={{ color: '#888', textDecoration: 'underline dotted' }}>@GinghamTree</a>
        </Typography>
      </Box>
    </Box>
  );
};

export default MainLayout; 